import { Comments } from "../../API/types/comments.types";
import { useState } from "react";
import { Delete, Edit } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import {
  useApiDeleteComment,
  useApiUpdateComment,
} from "../../API/comments.api";
import { useAuthStore } from "../../Zustand/auth.store";

type CommentProps = {
  commentData: Comments;
  onChanged?: () => void;
};

const Comment = ({ commentData, onChanged }: CommentProps) => {
  // TODO Add workday username validation to see if the user can update a comment
  /**
   * Custom hooks
   */
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const workdayUsername = useAuthStore((s) => s.workdayUsername);

  /**
   * States
   */
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editValue, setEditValue] = useState(commentData.comment);

  const date = new Date(commentData.createdAt);

  const formatedDate = new Intl.DateTimeFormat("en-US", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);

  const withinTwoHours = (() => {
    const created = new Date(commentData.createdAt).getTime();
    const now = Date.now();
    const twoHoursMs = 2 * 60 * 60 * 1000;
    return now - created <= twoHoursMs;
  })();

  const { call: callDelete } = useApiDeleteComment({
    onCompleted: () => {
      setDeleteOpen(false);
      onChanged && onChanged();
    },
  });

  const { call: callUpdate } = useApiUpdateComment({
    onCompleted: () => {
      setEditOpen(false);
      onChanged && onChanged();
    },
  });

  const handleConfirmDelete = () => {
    callDelete(commentData.id);
  };

  const handleConfirmEdit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!editValue.trim()) return;
    callUpdate(commentData.id, { updatedComment: editValue.trim() });
  };

  return (
    <div className="w-[95%] mb-5 mx-auto">
      <div className="flex gap-5 items-start justify-between">
        <div className="flex gap-5">
          <p className="font-semibold"> {commentData.userName} </p>
          <p className="text-gray-400">{formatedDate}</p>
        </div>
        <div className="flex gap-2">
          {withinTwoHours &&
            workdayUsername === commentData.workdayUsername && (
              <IconButton
                aria-label="edit"
                size="small"
                color="primary"
                onClick={() => setEditOpen(true)}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
          {isAdmin && (
            <IconButton
              aria-label="delete"
              size="small"
              sx={{ color: "#ef4444" }}
              onClick={() => setDeleteOpen(true)}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </div>
      </div>
      <p className="mt-3">{commentData.comment}</p>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete comment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this comment? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <form onSubmit={handleConfirmEdit} autoComplete="off">
          <DialogTitle>Edit comment</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="edit-comment"
              label="Comment"
              type="text"
              fullWidth
              multiline
              rows={4}
              inputProps={{ maxLength: 300 }}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              helperText={`${editValue.length}/300`}
              variant="standard"
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!editValue}>
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default Comment;
